package at.theatervogelweide.admin.contract

import kotlinx.coroutines.DelicateCoroutinesApi
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.ensureActive
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.emitAll
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.isActive
import kotlinx.rpc.RpcCall
import kotlinx.rpc.RpcClient
import kotlinx.rpc.krpc.ktor.client.KtorRpcClient

/**
 * An [RpcClient] that opens a new kotlinx.rpc connection once the current one has closed, e.g. after a proxy's idle
 * timeout, a network change or a laptop's sleep (#53). Services from `withService` follow it, since every call asks
 * for the current connection.
 *
 * Only a call that is under way while the connection closes fails; the next one connects again. When a connection
 * can't be opened, [onConnectFailure] hears of it, and the next call tries again. The browser doesn't say why an
 * upgrade failed, so the caller checks whether the session has expired.
 */
class ReconnectingRpcClient(
    private val connect: () -> KtorRpcClient,
    private val onConnectFailure: (Throwable) -> Unit = {},
) : RpcClient {
    private var connection: KtorRpcClient? = null
    private var closed = false

    private fun current(): KtorRpcClient {
        check(!closed) { "The RPC client was closed" }
        connection?.takeUnless { it.isConnectionClosed() }?.let { return it }
        connection?.let(::drop)
        return connect().also { connection = it }
    }

    override suspend fun <T> call(call: RpcCall): T {
        val connection = current()
        return try {
            connection.call(call)
        } catch (exception: Throwable) {
            // the caller went away (e.g. the screen closed); the connection is fine
            currentCoroutineContext().ensureActive()

            when {
                // the upgrade failed: nothing was opened
                !connection.webSocketSession.isCompleted -> {
                    drop(connection)
                    onConnectFailure(exception)
                }

                connection.isConnectionClosed() -> drop(connection)
            }
            throw exception
        }
    }

    override fun <T> callServerStreaming(call: RpcCall): Flow<T> = flow { emitAll(current().callServerStreaming(call)) }

    fun close() {
        closed = true
        connection?.let(::drop)
    }

    private fun drop(connection: KtorRpcClient) {
        connection.close()
        if (this.connection === connection) {
            this.connection = null
        }
    }
}

@OptIn(ExperimentalCoroutinesApi::class, DelicateCoroutinesApi::class)
private fun KtorRpcClient.isConnectionClosed(): Boolean {
    if (!webSocketSession.isCompleted) {
        return false
    }
    val session = webSocketSession.getCompleted()
    return !session.isActive || session.incoming.isClosedForReceive || session.outgoing.isClosedForSend
}
